import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogClose } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormGroup, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { ToastNotificationService } from '../../../../services/toast-notification.service';
import { ReportCreate } from '../../../../models/report.model';
import { debounceTime } from 'rxjs';
import { LayerService } from '../../../../services/layer.service';
import { AreaService } from '../../../../services/area.service';
import { NewReportService } from '../../../../services/newReport.service';
import { safeBase64Encode } from '../../../../utils';
import { NgStyle, NgClass } from '@angular/common';
import { MatFormField, MatInput, MatError } from '@angular/material/input';
import { InlineSvgComponent } from '../../../../components/inline-svg/inline-svg.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSlider, MatSliderRangeThumb } from '@angular/material/slider';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'frmg-risk-report-dialog',
  templateUrl: './risk-report-modal.component.html',
  styleUrls: ['./risk-report-modal.component.scss'],
  imports: [ReactiveFormsModule, NgStyle, MatFormField, MatInput, MatError, InlineSvgComponent, MatTooltip, MatSlideToggle, NgClass, MatCheckbox, MatSlider, MatSliderRangeThumb, MatButton, MatDialogClose]
})
export class RiskReportModalComponent implements OnInit {
  public saveRiskReportGroup: FormGroup;
  public warningTitle: string = 'Risk calculation is available for using \'points\' only.';
  public warningBody: string = 'Please note that risk calculation functionality is currently unavailable for polygons, rectangles, and lines. If any of these options are selected, they will not be employed in report generation.';
  public errorTitle: string = 'Risk calculation is available for using \'points\' only.';
  public riskTooltip: string = 'This feature sets the range of Exposure values of interest for risk calculation mapping. Values below or above the threshold range are not included in the generated risk report map (they are greyed out). The threshold range helps focus on critical areas of concern.';
  public boundaryTooltip: string = 'This features allows you to add and adjust a circular radius around your point of interest. This boundary is used to focus your area of interest and analysis.';
  public nearestTooltip: string = 'This feature identifies and marks the nearest high-risk point. High-risk areas are determined by the Nearest Risk Threshold. ';
  public extremePointsTooltip: string = 'This feature sets the range of Exposure values to be considered for nearest high-risk calculation.';
  public zoneTooltip: string = 'This features creates bands around your point or area of interest. The average fire risk is calculated within each band.';
  public hasPoints: boolean = true;
  public isLoading: boolean = false;
  public isAdvancedOpened: boolean;

  constructor(
    private dialogRef: MatDialogRef<RiskReportModalComponent>,
    private layerService: LayerService,
    @Inject(MAT_DIALOG_DATA) public data: {
      project_id: string,
      root_project_id: string,
      include_area_ids?: string[],
      exclude_area_ids?: string[],
      filters: any
    },
    private fb: FormBuilder,
    private toastNotificationService: ToastNotificationService,
    private reportService: NewReportService,
    private areaService: AreaService
  ) {
    this.saveRiskReportGroup = this.fb.group({
      name: ['', [Validators.required]],
      boundary: [2000, [Validators.required, Validators.min(10), Validators.max(2000)]],
      threshold: [0, [Validators.required]],
      enableExtremePoints: [true, [Validators.required]],
      layersThresholds: this.fb.array([], [Validators.required, this.layersValidation()]),
      zone_config: this.fb.group({
        enableZone: [false, [Validators.required]],
        zone_type: ['behind', [Validators.required]],
        point_num_circles: [2, [Validators.required, Validators.min(1)]],
        zone_width: [200, [Validators.required, Validators.min(1)]],
        target_box_count: [12, [Validators.required, Validators.min(1)]],
        zone_color: ['#74eb34', [Validators.required]],
        zone_alpha: [0.3, [Validators.required, Validators.min(0), Validators.max(1)]]
      })
      // risk_threshold: this.fb.group({
      //   enableNearestRisk: [true, [Validators.required]],
      //   nearest_risk_threshold_min: [0, [Validators.required]],
      //   nearest_risk_threshold_max: [200, [Validators.required]]
      // })
    });

    this.layerService.getLayers().subscribe(_ => {
      // TO-DO
    });

    this.saveRiskReportGroup.get('threshold').valueChanges.pipe(debounceTime(250)).subscribe(value => {
      if (value !== this.saveRiskReportGroup.value.risk_threshold.nearest_risk_threshold_min) {
        this.saveRiskReportGroup.get('risk_threshold').get('nearest_risk_threshold_min').setValue(this.saveRiskReportGroup.value.threshold, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    // this.hasPoints = this.data.some(area => area.geometry.type === "Point" || area.geometry.type === "MultiPoint");
  }

  private layersValidation(): ValidatorFn {
    return (formArray: FormArray) => {
      const formArrayValues = formArray.value;
      const haveEnabled = formArrayValues.some(layer => layer.enabled);
      return !haveEnabled ? { allDisabled: true } : null;
    };
  }

  public getLayersThresholdsControls() {
    return (this.saveRiskReportGroup.get('layersThresholds') as FormArray).controls.filter(el => el.value.name !== 'Exposure') as FormGroup[];
  }

  public getLayersLegendsControls(layer: FormGroup) {
    return (layer.get('legends') as FormArray).controls as FormGroup[];
  }

  public capitalizeFirstLetter(line) {
    return String(line).charAt(0).toUpperCase() + String(line).slice(1);
  }

  public closeDialog(result?: any): void {
    this.dialogRef.close(result);
  }

  public save(): void {
    this.isLoading = true;
    const config = this.saveRiskReportGroup.value;

    const filter: {
      project?: string,
      include?: string,
      exclude?: string
      limit?: number
    } = {
      limit: 100
    };
    if (this.data.include_area_ids && this.data.include_area_ids.length) {
      filter.include = this.data.include_area_ids.join(',');
    } else if (this.data.exclude_area_ids && this.data.exclude_area_ids.length) {
      filter.exclude = this.data.exclude_area_ids.join(',');
    }
    filter.project = this.data.project_id;
    this.areaService.getAreaByFilter(filter).subscribe(geoJson => {
      const jsonString = JSON.stringify(geoJson, null, 2);
      const utf8Bytes = new TextEncoder().encode(jsonString);
      const base64String = safeBase64Encode(utf8Bytes);
      const dataUrl = `data:application/geo+json;base64,${base64String}`;

      const layer_selection = {
        categorical_selections: {},
        layers: [],
        thresholds: {}
      };
      config.layersThresholds.filter(layer => layer.enabled).map(layer => {
        if (layer.enabled) {
          if (layer.discrete) {
            layer_selection.categorical_selections[layer.name] = layer.legends.filter(legend => legend.enabled).map(legend => legend.name);
          } else {
            layer_selection.thresholds[layer.name] = {
              max_threshold: layer.thresholdMax,
              min_threshold: layer.threshold,
              invert: layer.inverted
            };
          }
          layer_selection.layers.push(layer.name);
        }
      });

      const report: ReportCreate = {
        geojson_file: dataUrl,
        layer_selection,
        name: config.name,
        report_name: config.name

      };
      this.reportService.createReport(report).subscribe(_ => {
        this.isLoading = false;
        this.toastNotificationService.showNotification({ type: 'success', message: 'Request submitted' });
        this.closeDialog();
      },
      (error) => {
        this.isLoading = false;
        const errorMsg = 'Error creating report: ';
        this.toastNotificationService.showNotification({
          type: 'error',
          message: errorMsg + Object.values(Object.values(error.error)[0])[0]
        });
      });
    });
  }

  validateNearestStart(value: number) {
    if (value < this.saveRiskReportGroup.value.threshold) {
      this.saveRiskReportGroup.get('risk_threshold').get('nearest_risk_threshold_min').setValue(this.saveRiskReportGroup.value.threshold, { emitEvent: false });
    }
  }
}
